import React, {useEffect, useRef, useState} from 'react';
import {isRight} from 'fp-ts/lib/Either';
import {FaChevronLeft, FaChevronRight, FaMapMarkerAlt, FaTimes} from 'react-icons/fa';
import Event from '../../interfaces/Event';
import {fetchEventsNear} from '../../api/endpoints';
import {useLocationContext} from '../../providers/LocationProvider';
import {useScreenSizeContext} from '../../providers/ScreenSizeProvider';
import {DateRangeMenu} from '../../utils/DropwdownUtils/DropdownUtils';
import {formatDateObjToMonthAndDay} from '../../utils/DateUtils/DateUtils';
import EventBar from '../Events/EventBar';
import LocationPicker from '../LocationPicker/LocationPicker';
import './ShowsNearSection.css';


interface ShowsNearSectionProps {
    pageSize?: number;
}

const MAX_VISIBLE_PAGE_BUTTONS = 5;

const ShowsNearSection: React.FC<ShowsNearSectionProps> = ({pageSize = 4}) => {
    const {location, requestCurrentLocation, isLocating} = useLocationContext();
    const {isMobile} = useScreenSizeContext();

    const apiPageRef = useRef(1);
    const datePickerRef = useRef<HTMLDivElement>(null);

    const [allEvents, setAllEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [noMoreEvents, setNoMoreEvents] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [error, setError] = useState<string | null>(null);

    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
    const [activeDatePicker, setActiveDatePicker] = useState(false);

    // Reset and reload when location changes
    useEffect(() => {
        if (!location) {
            setAllEvents([]);
            apiPageRef.current = 1;
            setNoMoreEvents(false);
            setCurrentPage(1);
            return;
        }
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            apiPageRef.current = 1;
            setNoMoreEvents(false);
            setCurrentPage(1);
            const result = await fetchEventsNear({lat: location.lat, lon: location.lon}, 1);
            if (cancelled) return;
            if (isRight(result)) {
                setAllEvents(result.right);
                if (result.right.length === 0) setNoMoreEvents(true);
            } else {
                setAllEvents([]);
                console.error(result.left);
            }
            setLoading(false);
        };
        load();
        return () => { cancelled = true; };
    }, [location?.lat, location?.lon]);

    // Click-outside closes the date picker (matches QueryEvents pattern)
    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            if (datePickerRef.current && e.target instanceof Node && !datePickerRef.current.contains(e.target)) {
                setActiveDatePicker(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    // Date filter changes reset to page 1
    useEffect(() => {
        setCurrentPage(1);
    }, [dateRange]);

    const eventsMatchingFilter = (() => {
        if (dateRange[0] === null || dateRange[1] === null) return allEvents;
        const start = dateRange[0];
        const end = new Date(dateRange[1]);
        end.setDate(end.getDate() + 1);
        return allEvents.filter(e => {
            if (!e.occursAtLocal) return true;
            const d = new Date(e.occursAtLocal);
            return d >= start && d <= end;
        });
    })();

    const totalKnownPages = Math.max(1, Math.ceil(eventsMatchingFilter.length / pageSize));
    const pageStart = (currentPage - 1) * pageSize;
    const visibleEvents = eventsMatchingFilter.slice(pageStart, pageStart + pageSize);
    const canFetchMore = !noMoreEvents && dateRange[0] === null;
    const isLastKnownPage = currentPage >= totalKnownPages;
    const canGoNext = currentPage < totalKnownPages || canFetchMore;
    const canGoPrev = currentPage > 1;

    // Build a windowed list of page numbers around the current page
    const pageNumbers: number[] = (() => {
        const half = Math.floor(MAX_VISIBLE_PAGE_BUTTONS / 2);
        let from = Math.max(1, currentPage - half);
        const to = Math.min(totalKnownPages, from + MAX_VISIBLE_PAGE_BUTTONS - 1);
        from = Math.max(1, to - MAX_VISIBLE_PAGE_BUTTONS + 1);
        const out: number[] = [];
        for (let i = from; i <= to; i++) out.push(i);
        return out;
    })();

    const fetchNextApiPage = async (): Promise<boolean> => {
        if (!location) return false;
        setLoadingMore(true);
        const result = await fetchEventsNear({lat: location.lat, lon: location.lon}, apiPageRef.current + 1);
        let didLoad = false;
        if (isRight(result)) {
            if (result.right.length > 0) {
                setAllEvents(prev => [...prev, ...result.right]);
                apiPageRef.current += 1;
                didLoad = true;
            } else {
                setNoMoreEvents(true);
            }
        } else {
            console.error(result.left);
        }
        setLoadingMore(false);
        return didLoad;
    };

    const goToPage = async (n: number) => {
        if (n < 1 || loadingMore) return;
        if (n > totalKnownPages) {
            if (!canFetchMore) return;
            const loaded = await fetchNextApiPage();
            if (!loaded) return;
        }
        setCurrentPage(n);
    };

    const handleNext = () => {
        if (isLastKnownPage && canFetchMore) {
            goToPage(currentPage + 1);
        } else if (currentPage < totalKnownPages) {
            setCurrentPage(currentPage + 1);
        }
    };
    const handlePrev = () => {
        if (canGoPrev) setCurrentPage(currentPage - 1);
    };

    const handleUseCurrent = async () => {
        try {
            await requestCurrentLocation();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not get your location');
        }
    };

    const handleClearDate = () => setDateRange([null, null]);

    const dateFiltered = dateRange[1] !== null;
    const dateRangeString = dateRange[0] && dateRange[1]
        ? `${formatDateObjToMonthAndDay(dateRange[0])} - ${formatDateObjToMonthAndDay(dateRange[1])}`
        : '';

    return (
        <div className='shows-near-wrap'>
            <div className='shows-near-inner'>
                <div className='shows-near-card'>
                    <div className={`shows-near-header ${isMobile ? 'is-mobile' : ''}`}>
                        <div className='shows-near-title'>
                            <h2 className='shows-near-heading'>
                                Shows Near
                                <LocationPicker variant='inline' placeholderLabel='your city'/>
                            </h2>
                            {location && (
                                <span className='shows-near-subtitle'>· within 150 mi</span>
                            )}
                        </div>
                        {location && allEvents.length > 0 && (
                            <div className='shows-near-filters'>
                                <div className='relative' ref={datePickerRef}>
                                    <button
                                        type='button'
                                        className={`filter-button ${dateFiltered ? 'filtered' : ''}`}
                                        onClick={() => setActiveDatePicker(!activeDatePicker)}
                                    >
                                        {dateFiltered && !isMobile ? dateRangeString : 'Date'}
                                    </button>
                                    {activeDatePicker && (
                                        <DateRangeMenu
                                            startDate={dateRange[0]}
                                            endDate={dateRange[1]}
                                            onChange={(dates) => setDateRange(dates)}
                                            onClose={() => setActiveDatePicker(false)}
                                        />
                                    )}
                                </div>
                                {dateFiltered && (
                                    <button
                                        type='button'
                                        className='filter-button'
                                        onClick={handleClearDate}
                                        aria-label='Clear date filter'
                                        title='Clear date filter'
                                    >
                                        <div className='col'>
                                            <FaTimes/>
                                        </div>
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {!location && (
                        <div className='shows-near-empty no-location'>
                            <div className='shows-near-empty-icon'>
                                <FaMapMarkerAlt/>
                            </div>
                            <div className='shows-near-empty-text-block'>
                                <div className='shows-near-empty-title'>See what's playing near you</div>
                                <div className='shows-near-empty-text'>
                                    Pick a city above or use your current location.
                                </div>
                                {error && <div className='shows-near-error'>{error}</div>}
                            </div>
                            <div className='shows-near-empty-actions'>
                                <button
                                    type='button'
                                    className='shows-near-action-primary'
                                    onClick={handleUseCurrent}
                                    disabled={isLocating}
                                >
                                    {isLocating ? 'Locating…' : 'Use my location'}
                                </button>
                            </div>
                        </div>
                    )}

                    {location && loading && (
                        <div className='shows-near-loading'>
                            <div className='loading-spinner'/>
                        </div>
                    )}

                    {location && !loading && allEvents.length === 0 && (
                        <div className='shows-near-empty'>
                            <div className='shows-near-empty-icon'>
                                <FaMapMarkerAlt/>
                            </div>
                            <div className='shows-near-empty-text-block'>
                                <div className='shows-near-empty-title'>Nothing nearby right now</div>
                                <div className='shows-near-empty-text'>
                                    No upcoming shows within 150 miles of {location.label}. Try another city — the full schedule is below.
                                </div>
                            </div>
                        </div>
                    )}

                    {location && !loading && allEvents.length > 0 && eventsMatchingFilter.length === 0 && (
                        <div className='shows-near-empty'>
                            <div className='shows-near-empty-icon'>
                                <FaMapMarkerAlt/>
                            </div>
                            <div className='shows-near-empty-text-block'>
                                <div className='shows-near-empty-title'>No matches in that date range</div>
                                <div className='shows-near-empty-text'>
                                    No shows near {location.label} between {dateRangeString}. Try widening the dates.
                                </div>
                            </div>
                        </div>
                    )}

                    {location && !loading && visibleEvents.length > 0 && (
                        <>
                            <div className='shows-near-list'>
                                {visibleEvents.map(event => (
                                    <React.Fragment key={event.tevoEventId}>
                                        <EventBar event={event} isMobile={isMobile}/>
                                        {isMobile && <div className='divider-margin'/>}
                                    </React.Fragment>
                                ))}
                            </div>
                            {(canGoPrev || canGoNext || totalKnownPages > 1) && (
                                <div className='shows-near-pagination'>
                                    <button
                                        type='button'
                                        className='shows-near-page-btn shows-near-page-arrow'
                                        onClick={handlePrev}
                                        disabled={!canGoPrev || loadingMore}
                                        aria-label='Previous page'
                                    >
                                        <FaChevronLeft/>
                                    </button>
                                    {pageNumbers.map(n => (
                                        <button
                                            key={n}
                                            type='button'
                                            className={`shows-near-page-btn ${n === currentPage ? 'active' : ''}`}
                                            onClick={() => goToPage(n)}
                                            disabled={loadingMore}
                                        >
                                            {n}
                                        </button>
                                    ))}
                                    <button
                                        type='button'
                                        className='shows-near-page-btn shows-near-page-arrow'
                                        onClick={handleNext}
                                        disabled={!canGoNext || loadingMore}
                                        aria-label='Next page'
                                    >
                                        {loadingMore ? <span className='shows-near-page-spinner'/> : <FaChevronRight/>}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ShowsNearSection;
